<template>
  <Teleport to="body">
    <Transition name="modal" appear>
      <div 
        v-if="isVisible" 
        class="fixed inset-0 z-50 overflow-y-auto"
        @click="handleBackdropClick"
      >
        <!-- Backdrop -->
        <div class="fixed inset-0 bg-black bg-opacity-50 transition-opacity"></div>
        
        <!-- Modal Container -->
        <div class="flex min-h-full items-center justify-center p-4">
          <div 
            ref="modalRef"
            :class="[
              'relative bg-white dark:bg-gray-800 rounded-lg shadow-xl transition-all',
              'w-full max-w-md mx-auto',
              sizeClasses
            ]"
            @click.stop
          >
            <!-- Header -->
            <div v-if="title || $slots.header" class="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <div class="flex items-center justify-between">
                <slot name="header">
                  <h3 class="text-lg font-medium text-gray-900 dark:text-white">
                    {{ title }}
                  </h3>
                </slot>
                <button 
                  v-if="showCloseButton"
                  @click="close"
                  class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                >
                  <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                  </svg>
                </button>
              </div>
            </div>
            
            <!-- Body -->
            <div class="px-6 py-4">
              <slot name="body">
                <p v-if="message" class="text-gray-700 dark:text-gray-300">
                  {{ message }}
                </p>
              </slot>
            </div>
            
            <!-- Footer -->
            <div v-if="$slots.footer || showDefaultFooter" class="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
              <slot name="footer">
                <div class="flex justify-end space-x-3">
                  <button 
                    v-if="showCancelButton"
                    @click="cancel"
                    type="button"
                    class="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    {{ cancelText }}
                  </button>
                  <button 
                    v-if="showConfirmButton"
                    @click="confirm"
                    type="button"
                    :class="[
                      'px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white transition-colors',
                      confirmButtonClass
                    ]"
                  >
                    {{ confirmText }}
                  </button>
                </div>
              </slot>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick } from 'vue'
import { useGlobalModal } from '@/composables/useGlobalModal'

interface Props {
  title?: string
  message?: string
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full'
  showCloseButton?: boolean
  showDefaultFooter?: boolean
  showCancelButton?: boolean
  showConfirmButton?: boolean
  cancelText?: string
  confirmText?: string
  confirmButtonClass?: string
  closeOnBackdrop?: boolean
  closeOnEscape?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  size: 'md',
  showCloseButton: true,
  showDefaultFooter: false,
  showCancelButton: true,
  showConfirmButton: true,
  cancelText: 'Cancelar',
  confirmText: 'Confirmar',
  confirmButtonClass: 'bg-blue-600 hover:bg-blue-700',
  closeOnBackdrop: true,
  closeOnEscape: true
})

const emit = defineEmits<{
  close: []
  cancel: []
  confirm: []
}>()

const modalRef = ref<HTMLElement>()
const { isVisible, closeModal } = useGlobalModal()

const sizeClasses = computed(() => {
  switch (props.size) {
    case 'sm':
      return 'max-w-sm'
    case 'md':
      return 'max-w-md'
    case 'lg':
      return 'max-w-lg'
    case 'xl':
      return 'max-w-xl'
    case 'full':
      return 'max-w-full mx-4'
    default:
      return 'max-w-md'
  }
})

const close = () => {
  emit('close')
  closeModal()
}

const cancel = () => {
  emit('cancel')
  closeModal(false)
}

const confirm = () => {
  emit('confirm')
  closeModal(true)
}

const handleBackdropClick = () => {
  if (props.closeOnBackdrop) {
    close()
  }
}

const handleEscapeKey = (event: KeyboardEvent) => {
  if (event.key === 'Escape' && props.closeOnEscape && isVisible.value) {
    close()
  }
}

// Focus management
watch(isVisible, async (visible) => {
  if (visible) {
    await nextTick()
    modalRef.value?.focus()
    document.addEventListener('keydown', handleEscapeKey)
  } else {
    document.removeEventListener('keydown', handleEscapeKey)
  }
})
</script>

<style scoped>
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-active .relative,
.modal-leave-active .relative {
  transition: transform 0.3s ease;
}

.modal-enter-from .relative {
  transform: scale(0.95) translateY(-20px);
}

.modal-leave-to .relative {
  transform: scale(0.95) translateY(20px);
}
</style>